import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { businessApi } from "@/lib/api/endpoints";
import { readActiveBusinessId, writeActiveBusinessId } from "@/lib/api/tokens";
import type { Business, BusinessMembership, FeatureKey, MemberRole } from "@/lib/api/types";
import { normaliseHex } from "@/lib/color";
import { matchesFeature, roleAtLeast, subscriptionState } from "@/lib/plans";
import { qk } from "@/lib/query-keys";
import { useAuth } from "@/providers/auth-provider";
import { useColorScheme } from "@/providers/theme-provider";

interface BusinessContextValue {
  businessId: string | null;
  business: Business | null;
  membership: BusinessMembership | null;
  memberships: BusinessMembership[];
  isLoading: boolean;
  error: unknown;
  role: MemberRole | null;
  features: FeatureKey[];
  hasFeature: (key: FeatureKey) => boolean;
  canManage: (minimum?: MemberRole) => boolean;
  billing: ReturnType<typeof subscriptionState> | null;
  switchBusiness: (id: string) => void;
  refetchBusiness: () => void;
}

const BusinessContext = React.createContext<BusinessContextValue | null>(null);

export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const { businesses, status } = useAuth();
  const queryClient = useQueryClient();
  const { setBrandColor } = useColorScheme();
  const [selectedId, setSelectedId] = React.useState<string | null>(() => readActiveBusinessId());

  // Reconcile the stored id against what the user actually belongs to — a
  // membership can be revoked between visits.
  const businessId = React.useMemo(() => {
    if (!businesses.length) return null;
    const stored = selectedId && businesses.find((b) => b.id === selectedId)?.id;
    return stored ?? businesses[0].id;
  }, [businesses, selectedId]);

  React.useEffect(() => {
    if (businessId && businessId !== selectedId) {
      setSelectedId(businessId);
      writeActiveBusinessId(businessId);
    }
  }, [businessId, selectedId]);

  const businessQuery = useQuery({
    queryKey: businessId ? qk.business(businessId) : ["business", "none"],
    queryFn: () => businessApi.get(businessId as string),
    enabled: Boolean(businessId) && status === "authenticated",
    staleTime: 60_000,
  });

  const business = businessQuery.data ?? null;

  // Custom / logo-derived brand colour. `default` means our palette stands.
  React.useEffect(() => {
    const theme = business?.theme;
    if (!theme || theme.mode === "default") {
      setBrandColor(null);
      return;
    }
    const hex = normaliseHex(theme.mode === "logo" ? theme.logoPrimary : theme.primary);
    setBrandColor(hex ?? null);
  }, [business?.theme, setBrandColor]);

  const membership = React.useMemo(
    () => businesses.find((b) => b.id === businessId) ?? null,
    [businesses, businessId],
  );

  const role = business?.role ?? membership?.role ?? null;
  const features = business?.features ?? [];

  const switchBusiness = React.useCallback(
    (id: string) => {
      setSelectedId(id);
      writeActiveBusinessId(id);
      // Everything on screen belongs to the old business.
      queryClient.removeQueries({ queryKey: ["metrics"] });
      queryClient.removeQueries({ queryKey: ["todos"] });
      queryClient.removeQueries({ queryKey: ["gamification"] });
      queryClient.removeQueries({ queryKey: ["rai"] });
      queryClient.removeQueries({ queryKey: ["files"] });
      queryClient.removeQueries({ queryKey: ["portfolio"] });
      queryClient.removeQueries({ queryKey: ["education"] });
      queryClient.removeQueries({ queryKey: ["billing"] });
    },
    [queryClient],
  );

  const value = React.useMemo<BusinessContextValue>(
    () => ({
      businessId,
      business,
      membership,
      memberships: businesses,
      isLoading: businessQuery.isPending && Boolean(businessId),
      error: businessQuery.error,
      role,
      features,
      // Matches on the API's own vocabulary (rai_chat, file_upload, …) rather
      // than the short names the UI uses internally.
      hasFeature: (key) => matchesFeature(features, key),
      canManage: (minimum: MemberRole = "admin") => roleAtLeast(role, minimum),
      billing: business ? subscriptionState(business.subscription) : null,
      switchBusiness,
      refetchBusiness: () => void businessQuery.refetch(),
    }),
    [businessId, business, membership, businesses, businessQuery, role, features, switchBusiness],
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusinessContext() {
  const ctx = React.useContext(BusinessContext);
  if (!ctx) throw new Error("useBusinessContext must be used inside <BusinessProvider>");
  return ctx;
}

/** For the many components that cannot render without an active business. */
export function useBusinessId(): string {
  const { businessId } = useBusinessContext();
  if (!businessId) throw new Error("No active business");
  return businessId;
}
